import { EventEmitter, inject, Injectable, InjectionToken } from '@angular/core';
import { Observable, of, forkJoin, concat, defer, isObservable } from 'rxjs';
import { take, shareReplay, map, concatMap, switchMap } from 'rxjs/operators';

import { MissingTranslationHandler } from './missing-translation-handler';
import { TranslateCompiler } from './translate.compiler';
import { TranslateLoader } from './translate.loader';
import { TranslateParser } from './translate.parser';
import {
  TranslateStore,
  TranslationChangeEvent,
  LangChangeEvent,
  DefaultLangChangeEvent,
} from './translate.store';
import { TranslateDebugService } from './translate-debug.service';
import { isDefined, mergeDeep } from './util';

export const USE_STORE = new InjectionToken<boolean>('USE_STORE');
export const USE_DEFAULT_LANG = new InjectionToken<boolean>('USE_DEFAULT_LANG');
export const DEFAULT_LANGUAGE = new InjectionToken<string>('DEFAULT_LANGUAGE');
export const USE_EXTEND = new InjectionToken<boolean>('USE_EXTEND');

export interface TranslateServiceConfig {
  loader?: TranslateLoader;
  compiler?: TranslateCompiler;
  parser?: TranslateParser;
  missingTranslationHandler?: MissingTranslationHandler;
  isolate?: boolean;
  extend?: boolean;
  useDefaultLang?: boolean;
  defaultLanguage?: string;
}

@Injectable()
export class TranslateService {
  store = inject(TranslateStore);
  currentLoader = inject(TranslateLoader);
  compiler = inject(TranslateCompiler);
  parser = inject(TranslateParser);
  missingTranslationHandler = inject(MissingTranslationHandler);
  private useDefaultLang = inject(USE_DEFAULT_LANG, { optional: true }) ?? true;
  private isolate = inject(USE_STORE, { optional: true }) ?? false;
  private extend = inject(USE_EXTEND, { optional: true }) ?? false;
  private defaultLanguageToken = inject(DEFAULT_LANGUAGE, { optional: true });
  private debugService = inject(TranslateDebugService);

  private loadingTranslations!: Observable<any>;
  private pending = false;
  private _onTranslationChange: EventEmitter<TranslationChangeEvent> =
    new EventEmitter<TranslationChangeEvent>();
  private _onLangChange: EventEmitter<LangChangeEvent> = new EventEmitter<LangChangeEvent>();
  private _onDefaultLangChange: EventEmitter<DefaultLangChangeEvent> =
    new EventEmitter<DefaultLangChangeEvent>();
  private _defaultLang!: string;
  private _currentLang!: string;
  private _langs: string[] = [];
  private _translations: { [lang: string]: any } = {};
  private _translationRequests: { [lang: string]: Observable<any> } = {};

  constructor() {
    if (this.defaultLanguageToken) {
      this.setDefaultLang(this.defaultLanguageToken);
    }
  }

  get onTranslationChange(): EventEmitter<TranslationChangeEvent> {
    return this.isolate ? this._onTranslationChange : this.store.onTranslationChange;
  }

  get onLangChange(): EventEmitter<LangChangeEvent> {
    return this.isolate ? this._onLangChange : this.store.onLangChange;
  }

  get onDefaultLangChange(): EventEmitter<DefaultLangChangeEvent> {
    return this.isolate ? this._onDefaultLangChange : this.store.onDefaultLangChange;
  }

  get defaultLang(): string {
    return this.isolate ? this._defaultLang : this.store.defaultLang;
  }

  set defaultLang(defaultLang: string) {
    if (this.isolate) {
      this._defaultLang = defaultLang;
    } else {
      this.store.defaultLang = defaultLang;
    }
  }

  get currentLang(): string {
    return this.isolate ? this._currentLang : this.store.currentLang;
  }

  set currentLang(currentLang: string) {
    if (this.isolate) {
      this._currentLang = currentLang;
    } else {
      this.store.currentLang = currentLang;
    }
  }

  get langs(): string[] {
    return this.isolate ? this._langs : this.store.langs;
  }

  set langs(langs: string[]) {
    if (this.isolate) {
      this._langs = langs;
    } else {
      this.store.langs = langs;
    }
  }

  get translations(): { [lang: string]: any } {
    return this.isolate ? this._translations : this.store.translations;
  }

  set translations(translations: { [lang: string]: any }) {
    if (this.isolate) {
      this._translations = translations;
    } else {
      this.store.translations = translations;
    }
  }

  setDefaultLang(lang: string): void {
    if (lang === this.defaultLang) {
      return;
    }

    const pending = this.retrieveTranslations(lang);

    if (typeof pending !== 'undefined') {
      if (this.defaultLang == null) {
        this.defaultLang = lang;
      }

      pending.pipe(take(1)).subscribe(() => {
        this.changeDefaultLang(lang);
      });
    } else {
      this.changeDefaultLang(lang);
    }
  }

  getDefaultLang(): string {
    return this.defaultLang;
  }

  use(lang: string): Observable<any> {
    if (lang === this.currentLang && typeof this.translations[lang] !== 'undefined') {
      return of(this.translations[lang]);
    }

    const pending = this.retrieveTranslations(lang);

    if (typeof pending !== 'undefined') {
      if (!this.currentLang) {
        this.currentLang = lang;
      }

      return pending.pipe(
        map(() => {
          this.changeLang(lang);
          return this.translations[lang];
        }),
      );
    } else {
      this.changeLang(lang);
      return of(this.translations[lang]);
    }
  }

  private retrieveTranslations(lang: string): Observable<any> | undefined {
    let pending: Observable<any> | undefined;

    if (typeof this.translations[lang] === 'undefined' || this.extend) {
      this._translationRequests[lang] =
        this._translationRequests[lang] || this.getTranslation(lang);
      pending = this._translationRequests[lang];
    }

    return pending;
  }

  getTranslation(lang: string): Observable<any> {
    this.pending = true;
    const loadingTranslations = this.currentLoader
      .getTranslation(lang)
      .pipe(shareReplay(1), take(1));

    this.loadingTranslations = loadingTranslations.pipe(
      map((res: any) => {
        const compiled = this.compiler.compileTranslations(res, lang);
        this.translations[lang] =
          this.extend && this.translations[lang]
            ? { ...compiled, ...this.translations[lang] }
            : compiled;
        this.updateLangs();
        this.pending = false;
        return compiled;
      }),
      shareReplay(1),
      take(1),
    );

    return this.loadingTranslations;
  }

  setTranslation(lang: string, translations: any, shouldMerge = false): void {
    translations = this.compiler.compileTranslations(translations, lang);

    if ((shouldMerge || this.extend) && this.translations[lang]) {
      this.translations[lang] = mergeDeep(this.translations[lang], translations);
    } else {
      this.translations[lang] = translations;
    }

    this.updateLangs();
    this.onTranslationChange.emit({ lang: lang, translations: this.translations[lang] });
  }

  getLangs(): string[] {
    return this.langs;
  }

  addLangs(langs: string[]): void {
    langs.forEach((lang: string) => {
      if (this.langs.indexOf(lang) === -1) {
        this.langs.push(lang);
      }
    });
  }

  private updateLangs(): void {
    this.addLangs(Object.keys(this.translations));
  }

  getParsedResult(translations: any, key: string | string[], interpolateParams?: any): any {
    let res: any;

    if (key instanceof Array) {
      const result: { [key: string]: any } = {};
      let observables = false;

      for (const k of key) {
        result[k] = this.getParsedResult(translations, k, interpolateParams);
        if (isObservable(result[k])) {
          observables = true;
        }
      }

      if (observables) {
        const sources = key.map((k) => (isObservable(result[k]) ? result[k] : of(result[k])));
        return forkJoin(sources).pipe(
          map((arr: any[]) => {
            const obj: { [key: string]: any } = {};
            arr.forEach((value: any, index: number) => {
              obj[key[index]] = value;
            });
            return obj;
          }),
        );
      }

      return result;
    }

    if (translations) {
      res = this.parser.interpolate(this.parser.getValue(translations, key), interpolateParams);
    }

    if (
      typeof res === 'undefined' &&
      this.defaultLang != null &&
      this.defaultLang !== this.currentLang &&
      this.useDefaultLang
    ) {
      res = this.parser.interpolate(
        this.parser.getValue(this.translations[this.defaultLang], key),
        interpolateParams,
      );
    }

    if (typeof res === 'undefined') {
      const params: any = { key, translateService: this };
      if (typeof interpolateParams !== 'undefined') {
        params.interpolateParams = interpolateParams;
      }
      res = this.missingTranslationHandler.handle(params);
    }

    return typeof res !== 'undefined' ? res : key;
  }

  get(key: string | string[], interpolateParams?: any): Observable<string | any> {
    if (!isDefined(key) || !key.length) {
      throw new Error(`Parameter "key" required`);
    }

    if (this.pending) {
      return this.loadingTranslations.pipe(
        concatMap((res: any) => {
          res = this.getParsedResult(res, key, interpolateParams);
          return isObservable(res) ? res : of(res);
        }),
      );
    } else {
      const res = this.getParsedResult(this.translations[this.currentLang], key, interpolateParams);
      return isObservable(res) ? res : of(res);
    }
  }

  getStreamOnTranslationChange(
    key: string | string[],
    interpolateParams?: any,
  ): Observable<string | any> {
    if (!isDefined(key) || !key.length) {
      throw new Error(`Parameter "key" required`);
    }

    return concat(
      defer(() => this.get(key, interpolateParams)),
      this.onTranslationChange.pipe(
        switchMap((event: TranslationChangeEvent) => {
          const res = this.getParsedResult(event.translations, key, interpolateParams);
          if (typeof res.subscribe === 'function') {
            return res;
          } else {
            return of(res);
          }
        }),
      ),
    );
  }

  stream(key: string | string[], interpolateParams?: any): Observable<string | any> {
    if (!isDefined(key) || !key.length) {
      throw new Error(`Parameter "key" required`);
    }

    return concat(
      defer(() => this.get(key, interpolateParams)),
      this.onLangChange.pipe(
        switchMap((event: LangChangeEvent) => {
          const res = this.getParsedResult(event.translations, key, interpolateParams);
          return isObservable(res) ? res : of(res);
        }),
      ),
    );
  }

  instant(key: string | string[], interpolateParams?: any): string | any {
    if (!isDefined(key) || !key.length) {
      throw new Error(`Parameter "key" required`);
    }

    if (this.debugService.isDebug$.value) {
      if (key instanceof Array) {
        const obj: { [key: string]: any } = {};
        key.forEach((value: string, index: number) => {
          obj[key[index]] = key[index];
        });
        return obj;
      }
      return key;
    }

    const res = this.getParsedResult(this.translations[this.currentLang], key, interpolateParams);

    if (isObservable(res)) {
      if (key instanceof Array) {
        const obj: { [key: string]: any } = {};
        key.forEach((value: string, index: number) => {
          obj[key[index]] = key[index];
        });
        return obj;
      }
      return key;
    } else {
      return res;
    }
  }

  set(key: string, value: string, lang: string = this.currentLang): void {
    this.translations[lang][key] = this.compiler.compile(value, lang);
    this.updateLangs();
    this.onTranslationChange.emit({ lang: lang, translations: this.translations[lang] });
  }

  private changeLang(lang: string): void {
    this.currentLang = lang;
    this.onLangChange.emit({ lang: lang, translations: this.translations[lang] });

    if (this.defaultLang == null) {
      this.changeDefaultLang(lang);
    }
  }

  private changeDefaultLang(lang: string): void {
    this.defaultLang = lang;
    this.onDefaultLangChange.emit({ lang: lang, translations: this.translations[lang] });
  }

  reloadLang(lang: string): Observable<any> {
    this.resetLang(lang);
    return this.getTranslation(lang);
  }

  resetLang(lang: string): void {
    this._translationRequests[lang] = undefined!;
    this.translations[lang] = undefined;
  }

  getBrowserLang(): string | undefined {
    if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
      return undefined;
    }

    let browserLang: string | null = window.navigator.languages
      ? window.navigator.languages[0]
      : null;
    browserLang =
      browserLang ||
      (window.navigator as any).language ||
      (window.navigator as any).browserLanguage ||
      (window.navigator as any).userLanguage;

    if (browserLang == null) {
      return undefined;
    }

    if (browserLang.indexOf('-') !== -1) {
      browserLang = browserLang.split('-')[0];
    }

    if (browserLang.indexOf('_') !== -1) {
      browserLang = browserLang.split('_')[0];
    }

    return browserLang;
  }

  getBrowserCultureLang(): string | undefined {
    if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
      return undefined;
    }

    let browserCultureLang: string | null = window.navigator.languages
      ? window.navigator.languages[0]
      : null;
    browserCultureLang =
      browserCultureLang ||
      (window.navigator as any).language ||
      (window.navigator as any).browserLanguage ||
      (window.navigator as any).userLanguage;

    return browserCultureLang || undefined;
  }
}
